import { Caption1, Input, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { MoreHorizontal16Regular } from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogField, DialogGrid2 } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { DateField } from "@/components/date-field";
import { FuiButton, FuiInput } from "@/components/fui-control";
import { addDays, todayISO, type ISODate } from "@/engine/date";
import { useCan, useMoneyMasked } from "@/features/access/use-plan-access";
import { addCostActual, removeCostActual, setTaskActualCost } from "@/features/pm/pm-ops";
import { formatDateRange } from "@/lib/date-format";
import { fmtCurrency } from "@/lib/format";
import { newId } from "@/store/tree-ops";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  stack: { display: "flex", flexDirection: "column", gap: "16px", minWidth: 0, flex: 1, minHeight: 0 },
  block: { display: "flex", flexDirection: "column", gap: "8px", minWidth: 0, flexShrink: 0 },
  tasks: { flex: 1, minHeight: 0, overflow: "auto" },
  taskTable: { tableLayout: "fixed" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", minWidth: 0 },
  wbsCol: { width: "64px" },
  /** 입력칸과 헤더가 같은 오른쪽 끝을 쓴다. */
  amountCol: { width: "136px" },
  saveCol: { width: "88px" },
  fit: { height: "auto", paddingTop: "3px", paddingBottom: "3px" },
  amount: {
    width: "100%",
    minWidth: 0,
    "& input": { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  },
  money: { "& input": { textAlign: "right", fontVariantNumeric: "tabular-nums" } },
  fresh: { backgroundColor: tokens.colorNeutralBackground1Selected },
  quiet: { color: tokens.colorNeutralForeground3 },
  name: { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
});

function parseMoney(raw: string): number | null {
  const cleaned = raw.replaceAll(",", "").trim();
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value);
}

export function ActualCostPanel() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const project = useProjectStore((state) => state.project);
  const schedule = useProjectStore((state) => state.derived.schedule);
  const canEdit = useCan("plan.cost.edit");
  const { masked } = useMoneyMasked();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [start, setStart] = useState<ISODate | undefined>(todayISO);
  const [end, setEnd] = useState<ISODate | undefined>(() => addDays(todayISO(), 7));
  const [amount, setAmount] = useState("0");
  const [note, setNote] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [freshId, setFreshId] = useState<string | null>(null);
  const freshRef = useRef<HTMLTableRowElement>(null);

  const leaves = schedule.order.filter((id) => {
    const task = schedule.tasks.get(id);
    return task && !task.isSummary;
  });
  const names = new Map(project.tasks.map((task) => [task.id, task.name]));
  const costs = new Map(project.tasks.map((task) => [task.id, task.actualCost]));

  const clearDraft = (taskId: string) => {
    setDrafts((prev) => {
      if (prev[taskId] === undefined) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  const commitTask = (taskId: string) => {
    const raw = drafts[taskId];
    if (raw === undefined) return;
    const next = parseMoney(raw);
    if (raw.trim() && next == null) return;
    const current = costs.get(taskId) ?? null;
    if (next === current) {
      clearDraft(taskId);
      return;
    }
    useProjectStore.getState().commit("실제 원가", (plan) => setTaskActualCost(plan, taskId, next));
    clearDraft(taskId);
  };

  const rangeInvalid = !!start && !!end && end < start;
  const parsedAmount = parseMoney(amount);

  const openPeriod = () => {
    const startDate = todayISO();
    setStart(startDate);
    setEnd(addDays(startDate, 7));
    setAmount("0");
    setNote("");
    setPeriodOpen(true);
  };

  const addPeriod = () => {
    if (parsedAmount == null || !start || !end || rangeInvalid) return;
    const id = newId("ca");
    useProjectStore.getState().commit("기간 실제 원가", (plan) => addCostActual(plan, { periodStart: start, periodEnd: end, amount: parsedAmount, note }, id));
    setPeriodOpen(false);
    setAmount("0");
    setNote("");
    setFreshId(id);
  };

  useEffect(() => {
    if (!freshId) return;
    freshRef.current?.scrollIntoView({ block: "nearest" });
    const timer = window.setTimeout(() => setFreshId(null), 1800);
    return () => window.clearTimeout(timer);
  }, [freshId]);

  const periods = [...(project.costActuals ?? [])].reverse();

  return (
    <div className={styles.stack}>
      <div className={styles.block}>
        <div className={styles.head}>
          <Caption1>기간 원가</Caption1>
          {canEdit && !masked ? <FuiButton appearance="primary" onClick={openPeriod}>추가</FuiButton> : null}
        </div>
        {freshId ? <Caption1 className={styles.quiet} role="status">목록 맨 위에 추가했습니다.</Caption1> : null}
        <table className={table.table}>
          <thead>
            <tr>
              <th className={table.th}>기간</th>
              <th className={table.th}>메모</th>
              <th className={mergeClasses(table.th, table.num, styles.num)}>금액</th>
              <th className={table.th}>동작</th>
            </tr>
          </thead>
          <tbody>
            {periods.length === 0 ? (
              <tr>
                <td className={table.td} colSpan={4}><span className={styles.quiet}>기간 원가가 없습니다.</span></td>
              </tr>
            ) : periods.map((row) => (
              <tr key={row.id} ref={row.id === freshId ? freshRef : undefined} className={row.id === freshId ? styles.fresh : undefined}>
                <td className={table.td}>{formatDateRange(row.periodStart, row.periodEnd, "numeric")}</td>
                <td className={table.td}>{row.note || "—"}</td>
                <td className={mergeClasses(table.td, table.num, styles.num)}>{fmtCurrency(row.amount, masked)}</td>
                <td className={table.td}>
                  {canEdit ? (
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label="기간 원가 동작" />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem onClick={() => setPendingId(row.id)}>삭제</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  ) : <span className={styles.quiet}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.tasks}>
        <table className={mergeClasses(table.table, styles.taskTable)}>
          <colgroup>
            <col className={styles.wbsCol} />
            <col />
            <col className={styles.amountCol} />
            <col className={styles.saveCol} />
          </colgroup>
          <thead>
            <tr>
              <th className={mergeClasses(table.th, styles.wbsCol)}>WBS</th>
              <th className={table.th}>작업</th>
              <th className={mergeClasses(table.th, table.num, styles.num, styles.amountCol)}>실제 원가</th>
              <th className={mergeClasses(table.th, styles.saveCol)} />
            </tr>
          </thead>
          <tbody>
            {leaves.map((id) => {
              const scheduled = schedule.tasks.get(id);
              const current = costs.get(id);
              const raw = drafts[id];
              const shown = raw ?? (current == null || masked ? "" : String(current));
              const parsed = raw === undefined ? undefined : parseMoney(raw);
              const invalid = raw !== undefined && raw.trim() !== "" && parsed == null;
              const dirty = parsed !== undefined && !invalid && parsed !== (current ?? null);
              const taskName = names.get(id) ?? "작업";
              return (
                <tr key={id}>
                  <td className={mergeClasses(table.td, styles.fit, styles.wbsCol)}>{scheduled?.wbs ?? ""}</td>
                  <td className={mergeClasses(table.td, styles.fit)}><span className={styles.name}>{names.get(id) ?? "—"}</span></td>
                  <td className={mergeClasses(table.td, styles.fit, table.num, styles.num, styles.amountCol)}>
                    {masked ? (
                      fmtCurrency(current, true)
                    ) : (
                      <Input
                        size="small"
                        className={styles.amount}
                        aria-label={`${taskName} 실제 원가`}
                        aria-invalid={invalid || undefined}
                        value={shown}
                        disabled={!canEdit}
                        placeholder="—"
                        onChange={(_, data) => setDrafts((prev) => ({ ...prev, [id]: data.value }))}
                        onBlur={() => commitTask(id)}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter") return;
                          event.preventDefault();
                          commitTask(id);
                        }}
                      />
                    )}
                  </td>
                  <td className={mergeClasses(table.td, styles.fit, styles.saveCol)}>
                    {canEdit && !masked ? (
                      <FuiButton
                        appearance={dirty ? "primary" : "subtle"}
                        disabled={!dirty}
                        aria-label={`${taskName} 실제 원가 저장`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => commitTask(id)}
                      >
                        저장
                      </FuiButton>
                    ) : <span className={styles.quiet}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AppDialog
        open={periodOpen}
        onClose={() => setPeriodOpen(false)}
        title="기간 원가"
        footer={<DialogActionsBar onCancel={() => setPeriodOpen(false)} onConfirm={addPeriod} confirmLabel="추가" disabled={parsedAmount == null || !start || !end || rangeInvalid} />}
      >
        <DialogGrid2>
          <DialogField label="시작" required>
            <DateField size="sm" aria-label="기간 시작" value={start} onChange={setStart} />
          </DialogField>
          <DialogField label="종료" required hint={rangeInvalid ? "종료는 시작 이후입니다." : undefined}>
            <DateField size="sm" aria-label="기간 종료" value={end} onChange={setEnd} />
          </DialogField>
        </DialogGrid2>
        <DialogField label="금액" required>
          <FuiInput className={styles.money} density="dialog" aria-label="기간 금액" value={amount} onChange={(_, data) => setAmount(data.value)} onKeyDown={(event) => { if (event.key === "Enter") addPeriod(); }} />
        </DialogField>
        <DialogField label="메모">
          <FuiInput density="dialog" aria-label="기간 메모" value={note} onChange={(_, data) => setNote(data.value)} onKeyDown={(event) => { if (event.key === "Enter") addPeriod(); }} />
        </DialogField>
      </AppDialog>
      <ConfirmDialog
        open={pendingId != null}
        title="기간 원가 삭제"
        message="이 기간 원가를 삭제할까요? 비용 성과가 다시 계산됩니다."
        confirmLabel="삭제"
        onCancel={() => setPendingId(null)}
        onConfirm={() => {
          if (pendingId) useProjectStore.getState().commit("기간 원가 삭제", (plan) => removeCostActual(plan, pendingId));
          setPendingId(null);
        }}
      />
    </div>
  );
}
